/**
 * The `/demo` access proof (#2985).
 *
 * In "key required" mode the server issues a short-lived signed proof for a
 * correct access key (`POST /api/demo-sessions/access/key`). `apiFetch`
 * sends it as `X-Demo-Access` on every demo route. The server checks it on
 * each request, so this storage is a convenience, not the gate.
 *
 * sessionStorage: the proof lives only as long as the tab. `readDemoAccessProof`
 * self-expires past `expires_at`.
 */

const STORAGE_KEY = "tarii.demo_access.v1";

export const DEMO_ACCESS_HEADER = "X-Demo-Access";

export type DemoAccessMode = "closed" | "key" | "open";

export interface DemoAccessProof {
  proof: string;
  /** ISO timestamp, the server's expiry for this proof. */
  expires_at: string;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readDemoAccessProof(): string | null {
  const store = storage();
  const raw = store?.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DemoAccessProof;
    const expires = Date.parse(parsed.expires_at);
    if (!parsed.proof || !Number.isFinite(expires) || expires <= Date.now()) {
      clearDemoAccessProof();
      return null;
    }
    return parsed.proof;
  } catch {
    clearDemoAccessProof();
    return null;
  }
}

export function storeDemoAccessProof(proof: DemoAccessProof): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(proof));
}

export function clearDemoAccessProof(): void {
  storage()?.removeItem(STORAGE_KEY);
}

/** The demo routes the server gates: they get the proof header. The gate's
 * own routes (`/api/demo-sessions/access…`) need none. */
export function isGatedDemoPath(path: string): boolean {
  if (path.startsWith("/api/demo-sessions/access")) return false;
  return path.startsWith("/api/demo-sessions");
}
